import { callApi } from "@/services/api"
import { ActionContext } from "vuex"
import { CoinFlipRoomsAsMap, CoinFlipState, RootState } from "@/store/types"
import {
  CoinFlipRoom,
  GetRoomsQuery,
  Pagination,
  RoomCreateItem,
} from "@/services/types"
import { arrayToMap, mergeMaps } from "@/store/utils/rooms"
import { CasinoGameSlug, GameSlug, RoomStatuses } from "@/services/enums"

type Payload = {
  game_slug: GameSlug
  loadNextPage: boolean
  forPage: "default" | "my-games" | "history"
  statusIncludes: RoomStatuses[]
  isUserInRoom: boolean
}

type TResult = {
  data: CoinFlipRoomsAsMap
  pagination?: Pagination
}

type RoomResult = {
  data: CoinFlipRoom | null
}

type InviteBotResult = {
  data: any
}

type JoinOrUpdateRoomPayload = {
  room_id: number
  game_slug: GameSlug
  casino_game_slug: CasinoGameSlug
  items: RoomCreateItem[]
}

export type LeaveRoomPayload = {
  game_slug: GameSlug
  room_id: number
}

const queryByFilter = {
  default: {} as GetRoomsQuery,
  "my-games": {
    statusIncludes: [
      RoomStatuses.finished,
      RoomStatuses.pending,
      RoomStatuses.started,
    ],
    isUserInRoom: true,
  } as GetRoomsQuery,
  history: {
    statusIncludes: [RoomStatuses.finished],
  } as GetRoomsQuery,
}

export async function joinRoom(
  context: ActionContext<CoinFlipState, RootState>,
  payload: JoinOrUpdateRoomPayload
): Promise<RoomResult> {
  const { room_id, game_slug, casino_game_slug, items } = payload

  const response = await callApi.joinRoom<CoinFlipRoom>(
    room_id,
    game_slug,
    casino_game_slug,
    items
  )

  return {
    data: response.data.data,
  }
}

export async function leaveRoom(
  context: ActionContext<CoinFlipState, RootState>,
  payload: LeaveRoomPayload
): Promise<RoomResult> {
  const { room_id, game_slug } = payload

  const response = await callApi.leaveRoom<CoinFlipRoom>(
    game_slug,
    CasinoGameSlug.CoinFlip,
    room_id
  )

  return {
    data: response.data.data.participants.length ? response.data.data : null,
  }
}

export async function inviteBot(
  context: ActionContext<CoinFlipState, RootState>,
  payload: LeaveRoomPayload
): Promise<InviteBotResult> {
  const { room_id, game_slug } = payload
  const response = await callApi.inviteBot<CoinFlipRoom>(
    game_slug,
    CasinoGameSlug.CoinFlip,
    room_id
  )

  return {
    data: response.data.data,
  }
}

export async function getRoomsApi(
  context: ActionContext<CoinFlipState, RootState>,
  payload: Payload
): Promise<TResult> {
  const { game_slug, loadNextPage, forPage } = payload

  const page =
    loadNextPage && context.state.rooms.pagination
      ? context.state.rooms.pagination.next
      : undefined

  const response = await callApi.getRooms<CoinFlipRoom>(
    game_slug,
    CasinoGameSlug.CoinFlip,
    page,
    queryByFilter[forPage]
  )
  const responseDataToMap = arrayToMap(response.data.data, "id")

  return {
    data: page
      ? mergeMaps(context.state.rooms.data, responseDataToMap)
      : responseDataToMap,
    pagination: response.data.links,
  }
}
